import { ReactElement, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';

import AddOrdersActions from '~components/AddOrdersActions';
import DialogIntegrationsInfo from '~components/DialogIntegrationsInfo';
import DialogReports from '~components/DialogReports';
import DialogConfigAndPreferences from '~components/DialogConfigAndPreferences';

import { DashboardLoadingWrapper } from '../../../../Wrappers';

import DashboardVariantV2GiverContextProvider, {
  useDashboardVariantV2GiverContext,
} from './Context';
import DashboardVariantV2GiverInPreparationFiltersProvider, {
  useDashboardVariantV2GiverInPreparationFiltersContext,
} from './InPreparationFiltersContext';
import DashboardVariantV2GiverInTransitFiltersProvider from './InTransitFiltersContext';
import DashboardVariantV2GiverFinalizedFiltersProvider from './FinalizedFiltersContext';
import DashboardVariantV2GiverHeader from './Header';
import DashboardVariantV2GiverConnectYourStoreBanner from './ConnectYourStoreBanner';
import DashboardVariantV2GiverContent from './Content';

const DashboardVariantV2Giver = (): ReactElement => {
  const {
    activeTab: { getIsActiveTab, setActiveTab },
    statistics: { loading: loadingStatistics, reloadData: reloadStatistics },
    integrations: {
      loading: loadingIntegrations,
      data: integrations,
      reloadData: reloadIntegrations,
    },
    dialogs: {
      importFromFile: {
        open: openDialogImportFromFile,
        toggleOpen: toggleOpenDialogImportFromFile,
      },
      importFromIntegration: {
        open: openDialogImportFromIntegration,
        toggleOpen: toggleOpenDialogImportFromIntegration,
      },
      updateGeoError: { toggleOpen: toogleOpenDialogUpdateGeoError },
      reports: { open: openDialogReports, toggleOpen: toogleOpenDialogReports },
      userSatisfaction: {
        open: openDialogUserSatisfaction,
        toggleOpen: toogleOpenDialogUserSatisfaction,
      },
      heatmap: { open: openDialogHeatmap, toggleOpen: toogleOpenDialogHeatmap },
      integrations: {
        open: openDialogIntegrations,
        toggleOpen: toogleOpenDialogIntegrations,
      },
      configAndPreferences: {
        open: openDialogConfigAndPreferences,
        toggleOpen: toogleOpenDialogConfigAndPreferences,
      },
    },
  } = useDashboardVariantV2GiverContext();

  const {
    forceRequest: { toggleValue: toogleForceRequest },
  } = useDashboardVariantV2GiverInPreparationFiltersContext();

  const loading = useMemo(
    () => loadingStatistics || loadingIntegrations,
    [loadingStatistics, loadingIntegrations],
  );

  const handleEmptyByImportFromIntegrations = useCallback(() => {
    toggleOpenDialogImportFromIntegration(false);

    setTimeout(() => toogleOpenDialogIntegrations(true), 250);
  }, [toggleOpenDialogImportFromIntegration, toogleOpenDialogIntegrations]);

  const handleReloadInPreparationRequest = useCallback(() => {
    reloadStatistics();

    if (getIsActiveTab('inPreparation')) {
      toogleForceRequest(true);
      return;
    }

    setActiveTab('inPreparation');
  }, [getIsActiveTab, reloadStatistics, setActiveTab, toogleForceRequest]);

  return (
    <DashboardLoadingWrapper loading={loading}>
      <Stack spacing={2} flex={1}>
        <DashboardVariantV2GiverHeader />

        <DashboardVariantV2GiverConnectYourStoreBanner />

        <DashboardVariantV2GiverContent />

        <AddOrdersActions.ImportDialogs
          items={{
            fromFile: {
              open: openDialogImportFromFile,
              onClose: () => toggleOpenDialogImportFromFile(false),
              onUpdateErrors: () => toogleOpenDialogUpdateGeoError(true),
              enabledFetchAssociatedCompanies: false,
            },
            fromIntegration: {
              open: openDialogImportFromIntegration,
              onClose: () => toggleOpenDialogImportFromIntegration(false),
              onEmptyIntegrations: handleEmptyByImportFromIntegrations,
            },
          }}
          onSubmitImportSuccess={handleReloadInPreparationRequest}
        />

        {openDialogReports && (
          <DialogReports.Download
            open={openDialogReports}
            onClose={() => toogleOpenDialogReports(false)}
          />
        )}

        {openDialogUserSatisfaction && (
          <DialogReports.UserSatifaction
            open={openDialogUserSatisfaction}
            onClose={() => toogleOpenDialogUserSatisfaction(false)}
          />
        )}

        {openDialogHeatmap && (
          <DialogReports.Heatmap
            open={openDialogHeatmap}
            onClose={() => toogleOpenDialogHeatmap(false)}
          />
        )}

        <DialogIntegrationsInfo
          open={openDialogIntegrations}
          onClose={() => toogleOpenDialogIntegrations(false)}
          list={integrations}
          loading={false}
          reload={reloadIntegrations}
          hiddenTableEmpty
        />

        {openDialogConfigAndPreferences && (
          <DialogConfigAndPreferences
            open={openDialogConfigAndPreferences}
            onClose={() => toogleOpenDialogConfigAndPreferences(false)}
          />
        )}
      </Stack>
    </DashboardLoadingWrapper>
  );
};

const DashboardVariantV2GiverProviderWrapper = (): ReactElement => (
  <DashboardVariantV2GiverContextProvider>
    <DashboardVariantV2GiverInPreparationFiltersProvider>
      <DashboardVariantV2GiverInTransitFiltersProvider>
        <DashboardVariantV2GiverFinalizedFiltersProvider>
          <DashboardVariantV2Giver />
        </DashboardVariantV2GiverFinalizedFiltersProvider>
      </DashboardVariantV2GiverInTransitFiltersProvider>
    </DashboardVariantV2GiverInPreparationFiltersProvider>
  </DashboardVariantV2GiverContextProvider>
);

export default DashboardVariantV2GiverProviderWrapper;
